/**
 * The brief (docs/SPEC.md §25.2): an answer to the open question, an edit of
 * one line of the card, an accepted suggestion. Each answer is put into the
 * ad's query at once; what the worker does next arrives on the socket.
 *
 * Once the film exists an edit or an Accept is a change of the film written
 * by code (§5.5, P28-T04): the API answers 202 `{change: {id, n, state}}`,
 * and the ad's changes are read again (the change's events follow).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AdDetail, type BriefField, type BriefState, adKey } from "./ads";
import { changesKey } from "./changes";
import { type PricedAnswer, api, needsConfirm } from "./client";
import { ME_KEY } from "./me";

/** A card's write: the conversation before the film, a change after. */
export type CardAnswer =
  BriefState | { change: { id: string; n: number; state: string } };

export type AnswerBody =
  { text: string } | { choice: number } | { you_decide: true };

const path = (id: string) => `/v1/ads/${encodeURIComponent(id)}`;

/** Whether `next` (a write's answer) is not older than `held` (what the
 *  socket may already have brought): a 202 can arrive after the turn it
 *  started has finished and been published. */
export function isNewer(held: BriefState, next: BriefState): boolean {
  if (held.final && !next.final) return false;
  return next.turns.length >= held.turns.length;
}

function useSetBriefState(id: string) {
  const queryClient = useQueryClient();
  return (state: BriefState) =>
    queryClient.setQueryData<AdDetail>(adKey(id), (ad) =>
      ad && !needsConfirm(state) && isNewer(ad.brief_state, state)
        ? { ...ad, brief_state: state }
        : ad,
    );
}

/** `POST …/brief/answer` (202): the next turn runs on the worker; 409
 *  `brief_pending` while one runs. A turn is metered (§15.1): at or above
 *  `PRICE_CONFIRM_CREDITS` the route answers `{estimate, kind, confirm:
 *  "required"}` and starts nothing until sent again with `confirm: true`;
 *  a turn that starts holds credits, so the balance chip is read again. */
export function useAnswerBrief(id: string) {
  const set = useSetBriefState(id);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AnswerBody & { confirm?: boolean }) =>
      api<BriefState | PricedAnswer>(`${path(id)}/brief/answer`, {
        method: "POST",
        body,
      }),
    onSuccess: (answer) => {
      if (needsConfirm(answer)) return;
      set(answer as BriefState);
      void queryClient.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

function useSetCardAnswer(id: string) {
  const set = useSetBriefState(id);
  const queryClient = useQueryClient();
  return (answer: CardAnswer) => {
    if (needsConfirm(answer)) return;
    if ("change" in answer) {
      void queryClient.invalidateQueries({ queryKey: changesKey(id) });
    } else {
      set(answer);
    }
  };
}

/** `PATCH …/brief {field, value}`: one line of the card, `from: said`. */
export function useEditBrief(id: string) {
  const set = useSetCardAnswer(id);
  return useMutation({
    mutationFn: (body: { field: BriefField; value: unknown }) =>
      api<CardAnswer>(`${path(id)}/brief`, { method: "PATCH", body }),
    onSuccess: set,
  });
}

/** `POST …/brief/accept-suggestion {index, value}`: `value` is what a
 *  suggestion that asks for one (`needs`) is accepted with. */
export function useAcceptSuggestion(id: string) {
  const set = useSetCardAnswer(id);
  return useMutation({
    mutationFn: ({ index, value }: { index: number; value?: string }) =>
      api<CardAnswer>(`${path(id)}/brief/accept-suggestion`, {
        method: "POST",
        body: value === undefined ? { index } : { index, value },
      }),
    onSuccess: set,
  });
}
